<script>
	const Vue = require('vue');
	const metadata = require('core/metadata');
	const classService = require('services/classService');
	const model = require('model/model');
	const Q = require('q');
	const utilities = require('utilities');
	const userContext = require('core/userContext');

	const { authenticateWithGoogleClassroom, setGCClassList, isInvalidatingGoogleError } = require('utilities/googleClassroomUtilities');

	module.exports = {
		props: {
			onCancelClick: {
				default: function () {}
			},
			afterImport: {
				default: function () {}
			}
		},
		components: {
			'input-item-with-label': require('components/input-item-with-label.vue').default,
			'validated-form': require('components/validated-form.vue').default
		},
		modelData: ['gcAddon'],
		data() {
			let grades = metadata.gradeSelections.map(g => {
				return {
					id: g.id,
					text: g.text
				};
			});
			return {
				loading: true,
				classList: [],
				classById: {},
				selectedClassId: null,
				selectedClassIdModel: '',
				selectedGradeId: null,
				selectedGradeIdModel: null,
				gcp: true,
				tokenExpired: false,
				grades
			};
		},
		computed: {
			selectedClass() {
				let selectedClassId = this.selectedClassId;
				let classById = this.classById;
				let selectedClass = null;

				if (selectedClassId && classById[selectedClassId]) {
					selectedClass = classById[selectedClassId];
				}

				return selectedClass;
			},
			gcpItem() {
				return {
					text: 'Publish assignments to Google Classroom by default',
					id: 'google-classroom-publish',
					selected: this.gcp
				};
			}
		},
		methods: {
			onClassSelect(v) {
				this.selectedClassId = this.selectedClassIdModel;
			},
			onGradeSelect(v) {
				this.selectedGradeId = this.selectedGradeIdModel;
			},
			importClass() {
				let selectedClass = this.selectedClass;
				let g = String(this.selectedGradeId);
				let token = model.get('localGCAccessToken');
				if (selectedClass && g) {
					if (token) {
						let gcid = selectedClass.id;
						this.loading = true;
						return classService
							.createClassFromGoogleClassroom({
								token,
								gcid,
								g
							})
							.then(c => {
								let prms = Q();
								let gcp = this.gcp;

								utilities.closeModals();

								if (c && c.token) {
									userContext.setToken(c.token);
								}

								if (gcp === false) {
									prms = classService.assignmentSettings({
										cc: c.cc,
										gcp
									});
								}

								return prms.then(() => this.afterImport(c));
							})
							.catch(error => {
								if (isInvalidatingGoogleError(error)) {
									model.set('localGCAccessToken', '');
									this.tokenExpired = true;
								} else {
									throw error;
								}
							})
							.finally(c => {
								this.loading = false;
							});
					} else {
						this.tokenExpired = true;
					}
				}
				return Q.reject();
			},
			loadClasses() {
				let token = model.get('localGCAccessToken');
				if (userContext.loggedIn() && !userContext.isStudent()) {
					if (token) {
						this.loading = true;
						this.classList = [];
						this.classById = {};
						Q.all([
							setGCClassList(token).catch(error => {
								if (isInvalidatingGoogleError(error)) {
									model.set('localGCAccessToken', '');
									this.tokenExpired = true;
								} else {
									throw error;
								}
							}),
							classService.getClasses()
						])
							.spread((gcClasses, classes) => {
								if (gcClasses && gcClasses.length) {
									this.tokenExpired = false;
									let currentGCClasses = {};
									if (classes && classes.length) {
										currentGCClasses = classes.reduce((acc, c) => {
											if (c.gcid) {
												acc[c.gcid] = true;
											}
											return acc;
										}, {});
									}
									// Filter out inactive, sort by class name
									const gcAddonClass = this.gcAddon && this.gcAddon.courseId;
									this.classList = gcClasses
										.filter(c => {
											if (gcAddonClass) {
												return c.id === gcAddonClass;
											}
											return !currentGCClasses[c.id] && c.courseState === 'ACTIVE';
										})
										.sort((a, b) => {
											let textA = a.name.toUpperCase();
											let textB = b.name.toUpperCase();
											return textA < textB ? -1 : textA > textB ? 1 : 0;
										});
									// Map by GCID
									this.classById = this.classList.reduce((acc, c) => {
										acc[c.id] = c;
										return acc;
									}, {});

									if (gcAddonClass && this.classById[gcAddonClass]) {
										this.selectedClassIdModel = gcAddonClass;
										Vue.nextTick(() => {
											this.$refs.classField.dispatchEvent(new Event('change'));
										});
									}
								}
							})
							.finally(() => (this.loading = false));
					} else {
						this.tokenExpired = true;
					}
				}
			},
			onPublishChange(item) {
				this.gcp = !item.selected;
			},
			reauthenticateWithGoogleClassroom() {
				authenticateWithGoogleClassroom();
				// Listen for updated token and try again
				let remove = model.change('localGCAccessToken', (token, initial) => {
					if (!initial) {
						this.tokenExpired = false;
						this.loadClasses();
						remove();
					}
				});
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';

	form.google-classroom-import-form {
		max-width: 40rem;

		.loading-message {
			margin: 1rem;

			> a {
				display: block;
			}
		}

		.button-container {
			button.button {
				display: block;
				margin: 0 auto 0.5rem auto;
			}

			button.button.submit {
				margin-top: 2rem;
			}
		}
	}
</style>
<template>
	<validated-form
		class="form google-classroom-import-form"
		:on-submit="importClass">
		<div
			v-if="!loading && !classList.length && !tokenExpired"
			class="loading-message">
			You have no Google Classroom Classes to import at this time.
		</div>
		<div
			v-if="!loading && tokenExpired"
			class="loading-message">
			Your token has expired and you need to reauthenticate with Google Classroom.
			<button
				class="button margin-top-small"
				@click="reauthenticateWithGoogleClassroom">
				Authenticate with Google Classroom
			</button>
		</div>
		<div class="input-wrapper select-wrapper">
			<select
				class="class-select"
				v-model="selectedClassIdModel"
				@change="onClassSelect"
				:disabled="loading || !classList.length"
				v-touched-field
				required
				ref="classField">
				<option
					value=""
					selected></option>
				<option
					v-for="clazz in classList"
					:key="clazz.id"
					:value="clazz.id">
					{{ clazz.name + (clazz.section ? ': ' + clazz.section : '') }}
				</option>
			</select>
			<label
				placeholder="Please select a class to import"
				alt="Please select a class to import"></label>
		</div>
		<div class="date-select input-wrapper select-wrapper">
			<select
				v-model="selectedGradeIdModel"
				@change="onGradeSelect"
				:disabled="!selectedClass || loading"
				v-touched-field
				required>
				<option value=""></option>
				<option
					v-for="grade in grades"
					:key="grade.id"
					:value="grade.id">
					{{ grade.text }}
				</option>
			</select>
			<label
				placeholder="What grade is this class?"
				alt="What grade is this class?"></label>
		</div>
		<div class="input-checkbox">
			<input-item-with-label
				:item="gcpItem"
				:on-item-click="onPublishChange">
			</input-item-with-label>
		</div>
		<div class="button-container">
			<button
				type="submit"
				class="button submit"
				:class="{
					'-in-progress': loading
				}"
				:disabled="!selectedClass || !selectedGradeId || loading || tokenExpired">
				Import {{ selectedClass && selectedClass.name ? selectedClass.name : '' }} Class
			</button>
			<button
				class="button -outline"
				type="button"
				@click="onCancelClick">
				Cancel
			</button>
		</div>
	</validated-form>
</template>
